import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { 
  Plus, Search, Trash2, FileText, ChevronLeft, ChevronRight, X, 
  Download, Eye, Filter, AlertTriangle, Calendar, Pencil, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';
import DocumentUploadForm from '../components/DocumentUploadForm';

export type DocumentType = 'SLA' | 'CONTRACT' | 'INVOICE' | 'DELIVERY_NOTE' | 'CERTIFICATE' | 'OTHER';

export interface Document {
  id: string;
  name: string;
  type: DocumentType;
  description?: string;
  filePath: string;
  fileName: string;
  fileSize?: number;
  mimeType?: string;
  documentDate?: string;
  expiryDate?: string;
  uploadedAt: string;
  serviceProvider?: {
    id: string;
    name: string;
  };
  uploadedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface ServiceProvider {
  id: string;
  name: string;
}

export interface DocumentFormData {
  name: string;
  type: DocumentType;
  description: string;
  documentDate: string;
  expiryDate: string;
  serviceProviderId: string;
  file: File | null;
}

export const initialFormData: DocumentFormData = {
  name: '',
  type: 'OTHER',
  description: '',
  documentDate: '',
  expiryDate: '',
  serviceProviderId: '',
  file: null,
};

const DOCUMENT_TYPES: { value: DocumentType; label: string }[] = [
  { value: 'SLA', label: 'SLA' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'DELIVERY_NOTE', label: 'Delivery Note' },
  { value: 'CERTIFICATE', label: 'Certificate' },
  { value: 'OTHER', label: 'Other' },
];

const getTypeColor = (type: DocumentType) => {
  switch (type) {
    case 'SLA':
      return 'bg-blue-100 text-blue-800';
    case 'CONTRACT':
      return 'bg-purple-100 text-purple-800';
    case 'INVOICE':
      return 'bg-green-100 text-green-800';
    case 'DELIVERY_NOTE':
      return 'bg-orange-100 text-orange-800';
    case 'CERTIFICATE':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getTypeLabel = (type: DocumentType) => {
  return DOCUMENT_TYPES.find(t => t.value === type)?.label || type;
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
};

const isExpiringSoon = (expiryDate?: string) => {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return expiry > now && expiry <= thirtyDaysFromNow;
};

const isExpired = (expiryDate?: string) => {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
};

export default function DocumentsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState<string>('');
  const [filterProvider, setFilterProvider] = useState<string>('');
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<DocumentFormData>(initialFormData);

  // Handle action=upload query parameter to open upload modal
  useEffect(() => {
    if (searchParams.get('action') === 'upload') {
      setShowModal(true);
      // Remove action param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null);
  const [previewingDocument, setPreviewingDocument] = useState<Document | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  // Fetch documents
  const { data, isLoading } = useQuery({
    queryKey: ['documents', { page, search, filterType, filterProvider, showExpiringOnly }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (filterType) params.append('type', filterType);
      if (filterProvider) params.append('serviceProviderId', filterProvider);
      if (showExpiringOnly) params.append('expiringWithinDays', '30');
      const response = await api.get(`/documents?${params.toString()}`);
      return response.data;
    },
  });

  // Fetch service providers for filter
  const { data: providersData } = useQuery({
    queryKey: ['service-providers-list'],
    queryFn: async () => {
      const response = await api.get('/service-providers?limit=100');
      return response.data;
    },
  });

  // Fetch expiring documents count
  const { data: expiringData } = useQuery({
    queryKey: ['documents-expiring'],
    queryFn: async () => {
      const response = await api.get('/documents/expiring?days=30');
      return response.data;
    },
  });

  const documents: Document[] = data?.data || [];
  const pagination = data?.pagination;
  const providers: ServiceProvider[] = providersData?.data || [];
  const expiringCount = expiringData?.data?.length || 0;

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (data: DocumentFormData) => {
      const formDataObj = new FormData();
      formDataObj.append('name', data.name);
      formDataObj.append('type', data.type);
      if (data.description) formDataObj.append('description', data.description);
      if (data.documentDate) formDataObj.append('documentDate', data.documentDate);
      if (data.expiryDate) formDataObj.append('expiryDate', data.expiryDate);
      if (data.serviceProviderId) formDataObj.append('serviceProviderId', data.serviceProviderId);
      if (data.file) formDataObj.append('file', data.file);

      return api.post('/documents', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents-expiring'] });
      closeModal();
      toast({ title: 'Success', description: 'Document uploaded successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to upload document', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/documents/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['documents-expiring'] });
      setShowDeleteConfirm(false);
      setDeletingId(null);
      toast({ title: 'Success', description: 'Document deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete document', variant: 'destructive' });
    },
  });

  // Update name mutation
  const updateNameMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      return api.put(`/documents/${id}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setEditingDocId(null);
      setEditingName('');
      toast({ title: 'Success', description: 'Document name updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update document name', variant: 'destructive' });
    },
  });

  const openUploadModal = () => {
    setFormData(initialFormData);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData(initialFormData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.file) {
      toast({ title: 'Error', description: 'Please select a file', variant: 'destructive' });
      return;
    }
    uploadMutation.mutate(formData);
  };

  const handleDownload = async (doc: Document) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Error', description: 'Failed to download document', variant: 'destructive' });
    }
  };

  const confirmDelete = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const clearFilters = () => {
    setFilterType('');
    setFilterProvider('');
    setShowExpiringOnly(false);
    setPage(1);
  };

  const hasActiveFilters = filterType || filterProvider || showExpiringOnly;

  const startEditingName = (doc: Document) => {
    setEditingDocId(doc.id);
    setEditingName(doc.name);
  };

  const cancelEditingName = () => {
    setEditingDocId(null);
    setEditingName('');
  };

  const saveEditedName = (id: string) => {
    if (editingName.trim()) {
      updateNameMutation.mutate({ id, name: editingName.trim() });
    }
  };

  const canPreview = (mimeType?: string) => {
    if (!mimeType) return false;
    return mimeType.startsWith('image/') || mimeType === 'application/pdf';
  };

  const openPreview = async (doc: Document) => {
    setPreviewingDocument(doc);
    setPreviewLoading(true);
    setPreviewUrl(null);
    
    try {
      const response = await api.get(`/documents/${doc.id}/preview`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: doc.mimeType }));
      setPreviewUrl(url);
    } catch (error) {
      console.error('Preview error:', error);
      toast({ title: 'Error', description: 'Failed to load document preview', variant: 'destructive' });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewingDocument(null);
    setPreviewUrl(null);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Documents</h1>
          <p className="text-muted-foreground">Manage SLAs, contracts, invoices, and other documents</p>
        </div>
        <Button onClick={openUploadModal}>
          <Plus className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Expiring Soon Alert */}
      {expiringCount > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <div className="flex-1">
                <p className="font-medium text-orange-800">
                  {expiringCount} document{expiringCount > 1 ? 's' : ''} expiring within 30 days
                </p>
                <p className="text-sm text-orange-600">Review and renew expiring documents</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="border-orange-300 text-orange-700 hover:bg-orange-100"
                onClick={() => {
                  setShowExpiringOnly(true);
                  setShowFilters(true);
                }}
              >
                View Expiring
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search documents..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <Button
                variant={showFilters ? 'default' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-2 bg-primary-foreground text-primary rounded-full w-5 h-5 text-xs flex items-center justify-center">
                    !
                  </span>
                )}
              </Button>
            </div>

            {/* Filter Row */}
            {showFilters && (
              <div className="flex flex-wrap gap-4 pt-2 border-t">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium">Type</label>
                  <select
                    className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Types</option>
                    {DOCUMENT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium">Service Provider</label>
                  <select
                    className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[200px]"
                    value={filterProvider}
                    onChange={(e) => {
                      setFilterProvider(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Providers</option>
                    {providers.map((provider) => (
                      <option key={provider.id} value={provider.id}>
                        {provider.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium">Expiry</label>
                  <label className="flex items-center gap-2 h-10 px-3 rounded-md border border-input bg-background text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showExpiringOnly}
                      onChange={(e) => {
                        setShowExpiringOnly(e.target.checked);
                        setPage(1);
                      }}
                      className="rounded"
                    />
                    Expiring soon (30 days)
                  </label>
                </div>
                {hasActiveFilters && (
                  <div className="flex items-end">
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="w-4 h-4 mr-1" />
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No documents found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Name</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Provider</th>
                    <th className="text-left p-4 font-medium">Date</th>
                    <th className="text-left p-4 font-medium">Expiry</th>
                    <th className="text-left p-4 font-medium">Size</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-muted/50">
                      <td className="p-4">
                        <div>
                          {editingDocId === doc.id ? (
                            <div className="flex items-center gap-2">
                              <Input
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                className="h-8 text-sm"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEditedName(doc.id);
                                  if (e.key === 'Escape') cancelEditingName();
                                }}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => saveEditedName(doc.id)}
                                disabled={updateNameMutation.isPending}
                              >
                                <Check className="w-4 h-4 text-green-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={cancelEditingName}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="font-medium flex items-center gap-2">
                              <FileText className="w-4 h-4 text-muted-foreground" />
                              {doc.name}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => startEditingName(doc)}
                                title="Edit name"
                              >
                                <Pencil className="w-3 h-3 text-muted-foreground" />
                              </Button>
                            </div>
                          )}
                          {doc.description && (
                            <div className="text-sm text-muted-foreground truncate max-w-xs">{doc.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(doc.type)}`}>
                          {getTypeLabel(doc.type)}
                        </span>
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {doc.serviceProvider?.name || '-'}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {formatDate(doc.documentDate)}
                      </td>
                      <td className="p-4">
                        {doc.expiryDate ? (
                          <div className="flex items-center gap-1">
                            {isExpired(doc.expiryDate) ? (
                              <span className="flex items-center gap-1 text-red-600 text-sm">
                                <AlertTriangle className="w-4 h-4" />
                                Expired
                              </span>
                            ) : isExpiringSoon(doc.expiryDate) ? (
                              <span className="flex items-center gap-1 text-orange-600 text-sm">
                                <Calendar className="w-4 h-4" />
                                {formatDate(doc.expiryDate)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">{formatDate(doc.expiryDate)}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {formatFileSize(doc.fileSize)}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => canPreview(doc.mimeType) ? openPreview(doc) : setViewingDocument(doc)}
                            title={canPreview(doc.mimeType) ? "View Document" : "View Details"}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownload(doc)}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => confirmDelete(doc.id)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">Page {page} of {pagination.totalPages}</span>
              <Button variant="outline" size="icon" disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Upload Modal */}
      {showModal && (
        <DocumentUploadForm
          isOpen={showModal}
          onClose={closeModal}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
          providers={providers}
          isLoading={uploadMutation.isPending}
        />
      )}

      {/* View Document Modal */}
      {viewingDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg mx-4">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold">Document Details</h3>
                <Button variant="ghost" size="icon" onClick={() => setViewingDocument(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Name</label>
                    <p className="font-medium">{viewingDocument.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Type</label>
                    <p>
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(viewingDocument.type)}`}>
                        {getTypeLabel(viewingDocument.type)}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">File Name</label>
                    <p className="text-sm">{viewingDocument.fileName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">File Size</label>
                    <p>{formatFileSize(viewingDocument.fileSize)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Document Date</label>
                    <p>{formatDate(viewingDocument.documentDate)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Expiry Date</label>
                    <p className={isExpired(viewingDocument.expiryDate) ? 'text-red-600' : isExpiringSoon(viewingDocument.expiryDate) ? 'text-orange-600' : ''}>
                      {formatDate(viewingDocument.expiryDate)}
                      {isExpired(viewingDocument.expiryDate) && ' (Expired)'}
                      {isExpiringSoon(viewingDocument.expiryDate) && ' (Expiring Soon)'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Service Provider</label>
                    <p>{viewingDocument.serviceProvider?.name || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Uploaded By</label>
                    <p>{viewingDocument.uploadedBy ? `${viewingDocument.uploadedBy.firstName} ${viewingDocument.uploadedBy.lastName}` : '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-muted-foreground">Uploaded At</label>
                    <p>{new Date(viewingDocument.uploadedAt).toLocaleString()}</p>
                  </div>
                </div>
                {viewingDocument.description && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Description</label>
                    <p className="text-sm">{viewingDocument.description}</p>
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setViewingDocument(null)}>
                    Close
                  </Button>
                  <Button onClick={() => handleDownload(viewingDocument)}>
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Deletion</h3>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete this document? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deletingId && deleteMutation.mutate(deletingId)}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Document Preview Modal */}
      {previewingDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="w-full h-full max-w-6xl max-h-[90vh] mx-4 my-4 flex flex-col">
            <div className="flex items-center justify-between mb-2 text-white">
              <h3 className="text-lg font-semibold">{previewingDocument.name}</h3>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDownload(previewingDocument)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button variant="ghost" size="icon" onClick={closePreview} className="text-white hover:bg-white/20">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 bg-white rounded-lg overflow-hidden">
              {previewLoading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : previewUrl ? (
                previewingDocument.mimeType?.startsWith('image/') ? (
                  <div className="w-full h-full flex items-center justify-center p-4 bg-gray-100">
                    <img
                      src={previewUrl}
                      alt={previewingDocument.name}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : previewingDocument.mimeType === 'application/pdf' ? (
                  <iframe
                    src={previewUrl}
                    className="w-full h-full"
                    title={previewingDocument.name}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">Preview not available for this file type</p>
                      <Button className="mt-4" onClick={() => handleDownload(previewingDocument)}>
                        <Download className="w-4 h-4 mr-2" />
                        Download to View
                      </Button>
                    </div>
                  </div>
                )
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Failed to load preview</p>
                    <Button className="mt-4" onClick={() => handleDownload(previewingDocument)}>
                      <Download className="w-4 h-4 mr-2" />
                      Download to View
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
